import React, { useState, useEffect, useCallback, useRef } from "react";
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useSelector } from "react-redux";
import { selectUserDetails, selectMappedRoles } from "../../redux/slices/userSlice";
import { Alert, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import EngineeringPortalNoticeBanner from '../common/EngineeringPortalNoticeBanner';
// Import centralized date utilities
import { formatDateForForm, formatDateForTable } from "../../utils/dateUtils";

const normalizeIdentityValue = (value) => String(value || "").trim().toLowerCase();

const getUserIdentitySet = (userDetails) => new Set(
  [userDetails?.name, userDetails?.displayName, userDetails?.username]
    .map(normalizeIdentityValue)
    .filter(Boolean)
);

const entryBelongsToUser = (entry, userDetails) => {
  const userIdentities = getUserIdentitySet(userDetails);

  if (!userIdentities.size) {
    return false;
  }

  return [entry?.UserName, entry?.userName, entry?.EngineerId, entry?.engineerId]
    .map(normalizeIdentityValue)
    .filter(Boolean)
    .some((value) => userIdentities.has(value));
};

const EngineerPTO = ({ 
  ptoEntries = [], 
  loading = false, 
  error = null, 
  onCreatePto, 
  onUpdatePto, 
  onDeletePto, 
  userDropdowns,
  onRefreshUserDropdowns // Add new prop for re-fetching
}) => {
  const userDetails = useSelector(selectUserDetails);
  const mappedRoles = useSelector(selectMappedRoles);
  const currentUserName = userDetails?.name || userDetails?.displayName || userDetails?.username || "";

  console.log("user dropdowns", userDropdowns)
  
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  useRealtimeDraft(isAdding || isEditing);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [engineers, setEngineers] = useState([]);
  const hasTriggeredRefetch = useRef(false); // Track if we've already triggered a re-fetch
  const [formData, setFormData] = useState({
    EngineerId: "",
    UserName: "",
    StartDate: "",
    EndDate: "",
    Notes: "",
  });
  // Check if the current user is an admin
  const isAdmin = mappedRoles?.Admin || false;
  // Filter PTO entries based on user permissions
  const filteredPtoEntries = isAdmin 
    ? ptoEntries 
    : ptoEntries.filter((entry) => entryBelongsToUser(entry, userDetails));

    console.log(userDetails, "user details in PTO component");

  // Note: PTO entries are now fetched by parent component and passed as props  // Get all engineers from the dropdown data, similar to AssignedEngineeringJobs.js
  const getAllEngineers = useCallback(() => {
    const mechEngineers = userDropdowns?.mechOptions || [];
    const elecEngineers = userDropdowns?.elecOptions || [];
    const controlEngineers = userDropdowns?.controlOptions || [];
    
    // Check if all arrays are empty and trigger re-fetch if needed
    if (userDropdowns && 
        mechEngineers.length === 0 && 
        elecEngineers.length === 0 && 
        controlEngineers.length === 0 && 
        onRefreshUserDropdowns &&
        !hasTriggeredRefetch.current) {
      console.log('UserDropdowns are empty, triggering re-fetch...');
      hasTriggeredRefetch.current = true;
      onRefreshUserDropdowns();
      return []; // Return empty array while re-fetch is in progress
    }
    
    // Reset the flag when we have data
    if (mechEngineers.length > 0 || elecEngineers.length > 0 || controlEngineers.length > 0) {
      hasTriggeredRefetch.current = false;
    }
    
    // Combine all engineers and remove duplicates
    const allEngineers = [...mechEngineers, ...elecEngineers, ...controlEngineers];
    const uniqueEngineers = [...new Map(allEngineers.map(item => 
      [item.displayName, item])).values()];
      
    return uniqueEngineers.sort((a, b) => 
      a.displayName.localeCompare(b.displayName));
  }, [userDropdowns, onRefreshUserDropdowns]);
  // Initialize engineers list when userDropdowns data is available
  useEffect(() => {
    if (userDropdowns) {
      const engineersList = getAllEngineers();
      
      // Format engineers for the dropdown
      const formattedEngineers = engineersList.map(engineer => ({
        id: engineer.displayName, // Using displayName as ID for simplicity
        name: engineer.displayName
      }));
      
      // Find current user in the list or create entry if not found
      let currentUserInList = formattedEngineers.find(
        eng => eng.name === currentUserName
      );
      
      // If current user is not in the engineers list, add them
      if (!currentUserInList && currentUserName) {
        currentUserInList = {
          id: currentUserName,
          name: currentUserName
        };
        formattedEngineers.unshift(currentUserInList);
      }
      
      setEngineers(formattedEngineers);
      
      // Set current user as default in the form
      if (currentUserInList) {
        setFormData(prevForm => ({
          ...prevForm,
          EngineerId: currentUserInList.id,
          UserName: currentUserInList.name,
        }));
      }
    }}, [userDropdowns, currentUserName, getAllEngineers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log('handleInputChange:', { name, value });
    
    // If the engineer dropdown changes, update both ID and UserName
    if (name === "EngineerId") {
      const selectedEngineer = engineers.find(eng => eng.id === value);
      console.log('Engineer dropdown changed:', { value, selectedEngineer });
      if (selectedEngineer) {
        setFormData({
          ...formData,
          EngineerId: value,
          UserName: selectedEngineer.name
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };
  const resetForm = () => {
    // Reset form but keep the current engineer selected
    const currentUserInfo = engineers.find(eng => eng.name === currentUserName) || 
      { id: currentUserName, name: currentUserName };

    setFormData({
      EngineerId: currentUserInfo.id,
      UserName: currentUserInfo.name,
      StartDate: "",
      EndDate: "",
      Notes: "",
    });
    
    setIsAdding(false);
    setIsEditing(false);
    setCurrentEntry(null);
  };
  const handleAddPTO = async (e) => {
    e.preventDefault();
    try {
      await onCreatePto(formData);
      resetForm();
    } catch (error) {
      console.error('Error creating PTO entry:', error);
      // Error handling is managed by parent component
    }
  };
  const handleEditPTO = async (e) => {
    e.preventDefault();
    if (currentEntry) {
      try {
        const updateData = {
          id: currentEntry.id || currentEntry.ID,
          ...formData,
        };
        console.log('Updating PTO entry with data:', updateData);
        console.log('Current entry for reference:', currentEntry);
        
        await onUpdatePto(updateData);
        resetForm();
      } catch (error) {
        console.error('Error updating PTO entry:', error);
        // Error handling is managed by parent component
      }
    }
  };

  const handleDeletePTO = async (id) => {
    try {
      await onDeletePto(id);
    } catch (error) {
      console.error('Error deleting PTO entry:', error);
      // Error handling is managed by parent component
    }
  };  const startEditing = (entry) => {
    // Check if user can edit this entry
    if (!isAdmin && !entryBelongsToUser(entry, userDetails)) {
      alert("You can only edit your own PTO entries.");
      return;
    }
    
    console.log('Starting to edit PTO entry:', entry);
    console.log('Entry ID field check:', { 
      entryId: entry.id, 
      entryID: entry.ID, 
      finalId: entry.id || entry.ID 
    });
    console.log('Available engineers:', engineers);
    console.log('Entry UserName:', entry.UserName);
    
    // Find the engineer in our engineers list that matches this entry
    const matchingEngineer = engineers.find(eng => 
      eng.name === entry.UserName || eng.id === entry.UserName
    );
    
    console.log('Matching engineer found:', matchingEngineer);
      setCurrentEntry(entry);
    setFormData({
      EngineerId: matchingEngineer ? matchingEngineer.id : (entry.UserName || ""),
      UserName: entry.UserName || "",
      StartDate: formatDateForForm(entry.StartDate),
      EndDate: formatDateForForm(entry.EndDate),
      Notes: entry.Notes || "",
    });
    
    console.log('Form data set to:', {
      EngineerId: matchingEngineer ? matchingEngineer.id : (entry.UserName || ""),
      UserName: entry.UserName || "",
      StartDate: formatDateForForm(entry.StartDate),
      EndDate: formatDateForForm(entry.EndDate),
      Notes: entry.Notes || "",
    });
    
    setIsEditing(true);
    setIsAdding(false);
  };

  // Show loading or error states
  if (loading && !isAdding && !isEditing) {
    return (
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Engineer PTO Management</h5>
        </div>
        <div className="card-body text-center p-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading PTO entries...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <EngineeringPortalNoticeBanner />
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Engineer PTO Management</h5>
        {!isAdding && !isEditing && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setIsAdding(true);
              setIsEditing(false);
              setCurrentEntry(null);
                // Reset to current user when adding new entry
              const currentUserInfo = engineers.find(eng => eng.name === currentUserName) || 
                { id: currentUserName, name: currentUserName };
              
              setFormData(prev => ({
                ...prev,
                EngineerId: currentUserInfo.id,
                UserName: currentUserInfo.name,
                StartDate: "",
                EndDate: "",
                Notes: ""
              }));
            }}
          >
            <i className="bi bi-plus-circle"></i> Add PTO
          </button>
        )}
      </div>
      <div className="card-body">        {/* Show any API errors */}
        {error && <Alert variant="danger">{error}</Alert>}

        {(isAdding || isEditing) && (
          <form onSubmit={isEditing ? handleEditPTO : handleAddPTO}>            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">Engineer</label>
                {isAdmin ? (
                  <select
                    className="form-select"
                    name="EngineerId"
                    value={formData.EngineerId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select an engineer</option>
                    {engineers.map((engineer) => (
                      <option key={engineer.id} value={engineer.id}>
                        {engineer.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={currentUserName}
                    disabled
                    readOnly
                  />
                )}
              </div>
              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="StartDate"
                  value={formData.StartDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="EndDate"
                  value={formData.EndDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                name="Notes"
                value={formData.Notes}
                onChange={handleInputChange}
                rows="2"
                placeholder="Enter optional notes about this PTO"
              />
            </div>
            <div className="d-flex">              <button 
                type="submit" 
                className="btn btn-success me-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    {isEditing ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  isEditing ? (
                    <><i className="bi bi-check-circle"></i> Update</>
                  ) : (
                    <><i className="bi bi-check-circle"></i> Save</>
                  )
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                <i className="bi bi-x-circle"></i> Cancel
              </button>
            </div>
          </form>
        )}

        {!isAdding && !isEditing && (
          <div className="table-responsive">
            {filteredPtoEntries.length > 0 ? (
              <table className="table table-striped table-hover schedule-responsive-table">
                <thead>
                  <tr>
                    <th>Engineer</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPtoEntries.map((entry) => (                    <tr key={entry.id || entry.ID}>
                      <td data-label="Engineer">{entry.UserName || "Unknown"}</td>
                      <td data-label="Start Date">{formatDateForTable(entry.StartDate)}</td>
                      <td data-label="End Date">{formatDateForTable(entry.EndDate)}</td>
                      <td data-label="Notes">{entry.Notes}</td>
                      <td data-label="Actions">                        {/* Only show edit/delete buttons if user owns the entry or is admin */}
                        {(isAdmin || entryBelongsToUser(entry, userDetails)) && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => startEditing(entry)}
                              disabled={loading}
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"                              onClick={() => {
                                if (!isAdmin && !entryBelongsToUser(entry, userDetails)) {
                                  alert("You can only delete your own PTO entries.");
                                  return;
                                }
                                if (window.confirm('Are you sure you want to delete this PTO entry?')) {
                                  handleDeletePTO(entry.id || entry.ID);
                                }
                              }}
                              disabled={loading}
                            >
                              {loading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="bi bi-trash"></i>
                              )}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="alert alert-info">
                No PTO entries found. Click "Add PTO" to create one.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default EngineerPTO;
